import React, { useState, useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaLinkedin, FaEnvelope } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import HeroBackgroundLogs from './HeroBackgroundLogs';
import '../styles/Hero.css';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const socialVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const Hero = ({ onNavigate }) => {
  const { t } = useLanguage();
  const [titleIndex, setTitleIndex] = useState(0);
  const [typedTitle, setTypedTitle] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setTitleIndex((prevIndex) => (prevIndex + 1) % t.hero.titles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [t.hero.titles.length]);

  useEffect(() => {
    const currentTitle = t.hero.titles[titleIndex] || '';

    // Typewriter effect only for the second title (SOC Analyst Tier 1)
    if (titleIndex !== 1) {
      setTypedTitle(currentTitle);
      return undefined;
    }

    setTypedTitle('');
    let i = 0;
    const stepMs = 55;

    const id = window.setInterval(() => {
      i += 1;
      setTypedTitle(currentTitle.slice(0, i));
      if (i >= currentTitle.length) {
        window.clearInterval(id);
      }
    }, stepMs);

    return () => window.clearInterval(id);
  }, [t.hero.titles, titleIndex]);

  const handleViewProjects = (e) => {
    e.preventDefault();
    if (typeof onNavigate === 'function') {
      onNavigate('projects');
    }
  };

  return (
    <section id="hero" className="hero">
      <div className="hero-background">
        <HeroBackgroundLogs />
      </div>
      <div className="hero-container">
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.p className="hero-greeting" variants={itemVariants}>
            {t.hero.greeting}
          </motion.p>
          
          <motion.h1 className="hero-title" variants={itemVariants}>
            <span className="highlight">Diogo Cunha</span>
          </motion.h1>
          
          <motion.h2 className="hero-subtitle" variants={itemVariants}>
            <span className="typing-animation">{typedTitle}</span>
          </motion.h2>
          
          <motion.p className="hero-description" variants={itemVariants}>
            {t.hero.description}
          </motion.p>
          
          <motion.div className="hero-buttons" variants={itemVariants}>
            <motion.button 
              className="btn btn-primary" 
              onClick={handleViewProjects}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t.hero.viewProjects}
            </motion.button>
            <motion.a 
              href="/assets/d9f7e3a2b1c8/f4a1c7.pdf"
              className="btn btn-secondary" 
              download="Diogo_Cunha_CV.pdf"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t.hero.downloadCV}
            </motion.a>
          </motion.div>

          <motion.div 
            className="social-links"
            variants={itemVariants}
          >
            {[
              { href: "https://github.com/diogortcunha", icon: <FaGithub />, label: "GitHub" },
              { href: "https://linkedin.com/in/diogoreiscunha", icon: <FaLinkedin />, label: "LinkedIn" },
              { href: "mailto:diogortcunha@gmail.com", icon: <FaEnvelope />, label: "Email" },
            ].map((social, index) => (
              <motion.a
                key={social.label}
                href={social.href}
                target={social.href.startsWith('mailto') ? undefined : "_blank"}
                rel={social.href.startsWith('mailto') ? undefined : "noopener noreferrer"}
                className="social-icon"
                aria-label={social.label}
                variants={socialVariants}
                custom={index}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {social.icon}
              </motion.a>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default memo(Hero);
